import { Link, router } from "@inertiajs/react";
import { useState, useRef } from "react";
import { Plus, BriefcaseBusiness, MapPin, X, Loader2, Upload } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";
import { useAiJobStatus } from "../../hooks/useAiJobStatus";

interface Job {
  id: number;
  title: string | null;
  firm_name: string;
  locations: string[];
  practice_area: string | null;
  jd_year_range: string | null;
  source: string;
  sourcing_status: string;
  created_at: string;
}

interface Props {
  jobs: Job[];
  ai_assist: boolean;
}

const SOURCING_BADGE: Record<string, string> = {
  pending_evaluation: "bg-yellow-100 text-yellow-700",
  worth_sourcing:     "bg-green-100 text-green-700",
  sourced:            "bg-blue-100 text-blue-700",
  skipped:            "bg-gray-100 text-gray-500",
  failed:             "bg-red-100 text-red-600",
};

const SOURCE_LABEL: Record<string, string> = {
  leopard_alert: "Leopard",
  manual_entry:  "Manual",
  slack_forward: "Slack",
  firm_pdf:      "PDF",
};

type Tab = "paste" | "pdf" | "form";

const BLANK_FORM = {
  firm_name: "", title: "", practice_area: "",
  jd_year_range: "", job_type: "", description: "",
  locations: "",
};

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function JobsIndex({ jobs, ai_assist }: Props) {
  const [showModal, setShowModal] = useState(false);
  const [tab, setTab]             = useState<Tab>("paste");
  const [pasteText, setPasteText] = useState("");
  const [form, setForm]           = useState({ ...BLANK_FORM });
  const [pdfFile, setPdfFile]     = useState<File | null>(null);
  const [errors, setErrors]       = useState<Record<string, string>>({});
  const [extractJobId, setExtractJobId] = useState<number | null>(null);
  const [pdfJobId, setPdfJobId]         = useState<number | null>(null);
  const fileRef                   = useRef<HTMLInputElement>(null);

  // task 2dc337d2 Slice 3 — extraction runs as a background job now (finding
  // #38 INV-1: extracted fields only ever live in the job's result payload,
  // never written to a Job record — there isn't one yet on this modal).
  const { generating: extracting } = useAiJobStatus(extractJobId, {
    onReady:  (result) => { applyExtracted(result); setExtractJobId(null); },
    onFailed: () => { setErrors({ extract: "Extraction failed. Try again." }); setExtractJobId(null); },
  });
  const { generating: pdfExtracting } = useAiJobStatus(pdfJobId, {
    onReady:  (result) => { applyExtracted(result); setPdfJobId(null); },
    onFailed: () => { setErrors({ pdf: "PDF extraction failed. Try again." }); setPdfJobId(null); },
  });

  function closeModal() {
    setShowModal(false);
    setTab("paste");
    setPasteText("");
    setForm({ ...BLANK_FORM });
    setPdfFile(null);
    setErrors({});
    setExtractJobId(null);
    setPdfJobId(null);
  }

  function setField(key: keyof typeof BLANK_FORM, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function applyExtracted(data: Record<string, unknown>) {
    setForm({
      firm_name:     String(data.firm_name     ?? ""),
      title:         String(data.title         ?? ""),
      practice_area: String(data.practice_area ?? ""),
      jd_year_range: String(data.jd_year_range ?? ""),
      job_type:      String(data.job_type      ?? ""),
      description:   String(data.description   ?? ""),
      locations:     Array.isArray(data.locations)
        ? (data.locations as string[]).join(", ")
        : String(data.locations ?? ""),
    });
    setTab("form");
  }

  async function handleExtractText() {
    if (!pasteText.trim()) return;
    setErrors({});
    try {
      const res  = await fetch("/jobs/extract", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ text: pasteText }),
      });
      const data = await res.json();
      if (data.error || !data.ai_job_status_id) { setErrors({ extract: data.error ?? "Extraction failed. Try again." }); return; }
      setExtractJobId(data.ai_job_status_id);
    } catch {
      setErrors({ extract: "Extraction failed. Try again." });
    }
  }

  async function handleExtractPdf() {
    if (!pdfFile) return;
    setErrors({});
    const body = new FormData();
    body.append("file", pdfFile);
    try {
      const res  = await fetch("/jobs/extract_pdf", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken() },
        body,
      });
      const data = await res.json();
      if (data.error || !data.ai_job_status_id) { setErrors({ pdf: data.error ?? "PDF extraction failed. Try again." }); return; }
      setPdfJobId(data.ai_job_status_id);
    } catch {
      setErrors({ pdf: "PDF extraction failed. Try again." });
    }
  }

  function handleSubmit(source: string) {
    const locationArr = form.locations.split(",").map((l) => l.trim()).filter(Boolean);

    if (tab === "pdf" && pdfFile) {
      // FormData path for PDF upload — append locations individually
      const fd = new FormData();
      fd.append("job[firm_name]",     form.firm_name);
      fd.append("job[title]",         form.title);
      fd.append("job[practice_area]", form.practice_area);
      fd.append("job[jd_year_range]", form.jd_year_range);
      fd.append("job[job_type]",      form.job_type);
      fd.append("job[description]",   form.description);
      fd.append("source",             source);
      locationArr.forEach((l) => fd.append("job[locations][]", l));
      fd.append("source_document", pdfFile);
      router.post("/jobs", fd, { onSuccess: closeModal });
    } else {
      // Inertia nested object — supports arrays natively
      router.post("/jobs", {
        job: {
          firm_name:     form.firm_name,
          title:         form.title,
          practice_area: form.practice_area,
          jd_year_range: form.jd_year_range,
          job_type:      form.job_type,
          description:   form.description,
          locations:     locationArr,
        },
        source,
      }, { onSuccess: closeModal });
    }
  }

  return (
    <AppLayout title="Jobs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="text-sm text-gray-500 mt-0.5">{jobs.length} jobs</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors">
          <Plus size={14} />
          Add Job
        </button>
      </div>

      {/* Job list */}
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {jobs.length === 0 && (
          <div className="py-16 text-center">
            <BriefcaseBusiness className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No jobs yet. Add one to get started.</p>
          </div>
        )}

        {jobs.map((job) => (
          <div key={job.id} className="px-4 py-3 hover:bg-gray-50 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link href={`/jobs/${job.id}`} className="text-sm font-semibold text-gray-900 hover:text-green-700">
                  {job.firm_name}
                </Link>
                {job.title && <span className="text-sm text-gray-500 truncate">{job.title}</span>}
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${SOURCING_BADGE[job.sourcing_status] ?? "bg-gray-100 text-gray-500"}`}>
                  {job.sourcing_status.replace(/_/g, " ")}
                </span>
                <span className="text-xs text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">
                  {SOURCE_LABEL[job.source] ?? job.source}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {job.practice_area && <span className="text-xs text-gray-500">{job.practice_area}</span>}
                {job.jd_year_range && <span className="text-xs text-gray-400">JD {job.jd_year_range}</span>}
                {job.locations.length > 0 && (
                  <span className="text-xs text-gray-400 flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />{job.locations.join(", ")}
                  </span>
                )}
              </div>
            </div>
            <span className="text-xs text-gray-400 shrink-0">{fmt(job.created_at)}</span>
          </div>
        ))}
      </div>

      {/* Add Job modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Add Job</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-5">
              {(["paste", "pdf", "form"] as Tab[]).map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    tab === t
                      ? "border-green-700 text-green-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  {t === "paste" ? "Paste Text" : t === "pdf" ? "Upload PDF" : "Manual Form"}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Tab: Paste */}
              {tab === "paste" && (
                <div className="space-y-3">
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={8}
                    placeholder="Paste job description text here..."
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {errors.extract && <p className="text-xs text-red-600">{errors.extract}</p>}
                  <button onClick={handleExtractText} disabled={!pasteText.trim() || extracting || !ai_assist}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors">
                    {extracting ? <Loader2 size={13} className="animate-spin" /> : null}
                    {ai_assist ? "Extract with AI" : "AI Assist disabled"}
                  </button>
                  {!ai_assist && (
                    <p className="text-xs text-gray-400">Enable AI Assist in Settings to use text extraction.</p>
                  )}
                </div>
              )}

              {/* Tab: PDF */}
              {tab === "pdf" && (
                <div className="space-y-3">
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors">
                    <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      {pdfFile ? pdfFile.name : "Click to upload a PDF"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Firm job descriptions, email attachments</p>
                  </div>
                  <input ref={fileRef} type="file" accept=".pdf" className="hidden"
                    onChange={(e) => { setPdfFile(e.target.files?.[0] ?? null); setErrors({}); }} />
                  {errors.pdf && <p className="text-xs text-red-600">{errors.pdf}</p>}
                  <button onClick={handleExtractPdf} disabled={!pdfFile || pdfExtracting || !ai_assist}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors">
                    {pdfExtracting ? <Loader2 size={13} className="animate-spin" /> : null}
                    {ai_assist ? "Extract with AI" : "AI Assist disabled"}
                  </button>
                </div>
              )}

              {/* Tab: Form (also shown after extraction pre-fill) */}
              {tab === "form" && (
                <FormFields form={form} setField={setField} />
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              {tab === "form" && (
                <button
                  onClick={() => handleSubmit(pdfFile ? "firm_pdf" : "manual_entry")}
                  disabled={!form.firm_name.trim()}
                  className="px-4 py-2 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors">
                  Create Job
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

interface FormFieldsProps {
  form: typeof BLANK_FORM;
  setField: (key: keyof typeof BLANK_FORM, value: string) => void;
}

function FormFields({ form, setField }: FormFieldsProps) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">Firm Name *</label>
        <input value={form.firm_name} onChange={(e) => setField("firm_name", e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Whitmore & Hale LLP" />
      </div>
      <div className="col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
        <input value={form.title} onChange={(e) => setField("title", e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Corporate Associate" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Practice Area</label>
        <input value={form.practice_area} onChange={(e) => setField("practice_area", e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="CORPORATE" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">JD Year Range</label>
        <input value={form.jd_year_range} onChange={(e) => setField("jd_year_range", e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="3-5 or 2019-2022" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Job Type</label>
        <input value={form.job_type} onChange={(e) => setField("job_type", e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Associate" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Locations</label>
        <input value={form.locations} onChange={(e) => setField("locations", e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="New York, San Francisco" />
      </div>
      <div className="col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <textarea value={form.description} onChange={(e) => setField("description", e.target.value)}
          rows={5}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Job description..." />
      </div>
    </div>
  );
}

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}
